import type { ReactNode } from 'react';
import React, { type ElementType, useRef } from 'react';

import { CheckIcon } from 'components/atoms/Icons';
import type { IconSize } from 'components/atoms/Icons/BaseIcon';
import * as Styled from './PopupMenu.styled';

type IconPlacement = 'left' | 'right';

export interface PopupMenuItemProps
  extends React.HTMLAttributes<HTMLButtonElement> {
  forwardedAs?: ElementType;
  href?: string;
  target?: string;
  isDisabled?: boolean;
  hasIcon?: boolean;
  iconSize?: keyof typeof IconSize;
  icon?: ReactNode;
  border?: boolean;
  isChecked?: boolean;
  withBorderTop?: boolean;
  isCreate?: boolean;
  isDelete?: boolean;
  isClickable?: boolean;
  blurOnClick?: boolean;
  iconPlacement?: IconPlacement;
}

export { PopupMenuLabel } from './PopupMenu.styled';

export const PopupMenuItem: React.FC<PopupMenuItemProps> = ({
  forwardedAs = 'button',
  children,
  hasIcon,
  icon,
  iconSize = 'xs',
  isDisabled = false,
  isChecked = false,
  withBorderTop,
  isDelete = false,
  isCreate = false,
  isClickable = true,
  blurOnClick = false,
  onClick,
  iconPlacement = 'left',
  ...props
}) => {
  const ref = useRef<HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    onClick?.(event as any);
    if (blurOnClick) {
      ref.current?.blur();
    }
  };

  return (
    <Styled.PopupMenuLabel
      ref={ref}
      $clickable={isClickable}
      $create={isCreate}
      $delete={isDelete}
      $withBorderTop={withBorderTop}
      disabled={isDisabled}
      forwardedAs={forwardedAs}
      onClick={handleClick}
      {...props}
    >
      <div className="w-full flex items-center">
        {iconPlacement === 'right' && children}
        {(hasIcon || icon) && (
          <div className="flex w-sm2 h-full">
            {icon || (isChecked && <CheckIcon size={iconSize} />)}
          </div>
        )}
        {iconPlacement === 'left' && children}
      </div>
    </Styled.PopupMenuLabel>
  );
};
